import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useProject } from '../hooks/useProject'
import { useBoQ } from '../hooks/useBoQ'
import { useTasks } from '../hooks/useTasks'
import { LoadingSkeleton } from '../components/common/LoadingSkeleton'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/Tabs'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import { BarChart, Bar } from 'recharts'
import { PieChart, Pie, Cell } from 'recharts'
import { saveAs } from 'file-saver'

const CostControlPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const numericProjectId = projectId ? parseInt(projectId) : 0
  
  const [activeTab, setActiveTab] = useState('overview')
  
  const { data: project, isLoading: isLoadingProject } = useProject(numericProjectId)
  const { data: boqData, isLoading: isLoadingBoQ } = useBoQ(numericProjectId)
  const { data: tasksData, isLoading: isLoadingTasks } = useTasks(numericProjectId)
  
  useEffect(() => {
    if (!projectId) {
      navigate('/projects')
    }
  }, [projectId, navigate])
  
  if (isLoadingProject || isLoadingBoQ || isLoadingTasks) {
    return <LoadingSkeleton />
  }
  
  if (!project) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
          <p className="text-gray-500">Project not found</p>
        </div>
      </div>
    )
  }
  
  // Calculate total budget
  const totalBudget = boqData?.categories.reduce((sum, cat) => {
    return sum + (cat.totalValue || 0)
  }, 0) || 0
   
  // Calculate actual cost from tasks
  const actualCost = tasksData?.tasks.reduce((sum, task) => {
    return sum + (task.resources?.budget || 0)
  }, 0) || 0
   
  // Calculate variance
  const costVariance = totalBudget - actualCost
  const variancePercentage = totalBudget > 0 ? (costVariance / totalBudget) * 100 : 0
  
  // Calculate forecast (simple linear projection)
  const forecastData = []
  const completedTasks = tasksData?.tasks.filter(task => task.status === 'completed').length || 0
  const totalTasks = tasksData?.tasks.length || 1
  const completionRatio = completedTasks / totalTasks
  const forecastedCost = completionRatio > 0 ? (actualCost / completionRatio) : actualCost
  
  // Generate forecast data points
  for (let i = 0; i <= 10; i++) {
    forecastData.push({
      month: i,
      actual: actualCost * (i / 10),
      forecast: forecastedCost * (i / 10)
    })
  }
  
  // Budget warning threshold
  const budgetWarningThreshold = 0.9
  const isBudgetWarning = actualCost > totalBudget * budgetWarningThreshold
  const isBudgetOver = actualCost > totalBudget
  
  // Prepare data for charts
  const budgetByCategory = boqData?.categories.map(cat => ({
    name: cat.name,
    value: cat.totalValue || 0
  })) || []
  
  const costTrendData = tasksData?.tasks.map(task => ({
    name: task.taskName,
    actual: task.resources?.budget || 0,
    planned: (totalBudget / (tasksData.tasks.length || 1))
  })) || []
   
  // Export data function
  const exportData = () => {
    const data = {
      projectName: project.name,
      totalBudget,
      actualCost,
      costVariance,
      variancePercentage,
      categories: boqData?.categories || [],
      tasks: tasksData?.tasks || []
    }
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    saveAs(blob, `${project.name}_cost_control_data.json`)
  }
   
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6 shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{project.name}</h1>
            <p className="text-gray-600 mt-1">Cost Control & Budget Management</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={exportData}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm transition-colors"
            >
              Export Data
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
          <div className="bg-gray-50 p-3 rounded">
            <p className="text-gray-600">Total Budget</p>
            <p className="font-semibold text-gray-900">Rp {totalBudget.toLocaleString()}</p>
          </div>
          <div className="bg-gray-50 p-3 rounded">
            <p className="text-gray-600">Actual Cost</p>
            <p className="font-semibold text-gray-900">Rp {actualCost.toLocaleString()}</p>
          </div>
          <div className={`bg-gray-50 p-3 rounded ${costVariance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            <p className="text-gray-600">Variance</p>
            <p className="font-semibold">Rp {Math.abs(costVariance).toLocaleString()} {costVariance >= 0 ? '(Under)' : '(Over)'}</p>
          </div>
          <div className={`bg-gray-50 p-3 rounded ${variancePercentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            <p className="text-gray-600">Variance %</p>
            <p className="font-semibold">{variancePercentage.toFixed(1)}%</p>
          </div>
        </div>
        {isBudgetWarning && !isBudgetOver && (
          <div className="mt-4 p-3 bg-yellow-100 border-l-4 border-yellow-500 rounded-md">
            <p className="text-yellow-700 text-sm">
              <strong>Warning:</strong> Budget usage is approaching the limit ({((actualCost / totalBudget) * 100).toFixed(1)}% used)
            </p>
          </div>
        )}
        {isBudgetOver && (
          <div className="mt-4 p-3 bg-red-100 border-l-4 border-red-500 rounded-md">
            <p className="text-red-700 text-sm">
              <strong>Alert:</strong> Budget has been exceeded by Rp {(actualCost - totalBudget).toLocaleString()}
            </p>
          </div>
        )}
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="budget">Budget Breakdown</TabsTrigger>
          <TabsTrigger value="expenses">Actual Expenses</TabsTrigger>
          <TabsTrigger value="forecast">Forecast</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview">
          <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm mt-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Cost Overview</h2>
             
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium text-gray-700 mb-3">Budget Allocation</h3>
                <div className="space-y-3">
                  {boqData?.categories.map((category: any) => (
                    <div key={category.id} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                      <span className="font-medium text-gray-900">{category.name}</span>
                      <span className="text-gray-700">Rp {category.totalValue?.toLocaleString() || '0'}</span>
                    </div>
                  ))}
                </div>
              </div>
               
              <div>
                <h3 className="font-medium text-gray-700 mb-3">Cost Status</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                    <span className="text-gray-700">Planned Budget</span>
                    <span className="font-medium text-gray-900">Rp {totalBudget.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                    <span className="text-gray-700">Actual Spent</span>
                    <span className="font-medium text-gray-900">Rp {actualCost.toLocaleString()}</span>
                  </div>
                  <div className={`flex justify-between items-center p-3 bg-gray-50 rounded ${costVariance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    <span className="font-medium">Remaining Budget</span>
                    <span className="font-semibold">Rp {(totalBudget - actualCost).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6">
              <h3 className="font-medium text-gray-700 mb-3">Budget Distribution</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={budgetByCategory}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {budgetByCategory.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'][index % 5]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="budget">
          <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm mt-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Budget Breakdown by Category</h2>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-2">Category</th>
                    <th className="px-4 py-2">Code</th>
                    <th className="px-4 py-2">Budget</th>
                    <th className="px-4 py-2">Items</th>
                    <th className="px-4 py-2">% of Total</th>
                  </tr>
                </thead>
                <tbody>
                  {boqData?.categories.map((category: any) => (
                    <tr key={category.id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-2 font-medium text-gray-900">{category.name}</td>
                      <td className="px-4 py-2 text-gray-600">{category.code}</td>
                      <td className="px-4 py-2 text-gray-900">Rp {category.totalValue?.toLocaleString() || '0'}</td>
                      <td className="px-4 py-2 text-gray-600">{category.itemCount || 0}</td>
                      <td className="px-4 py-2 text-gray-600">{(category.totalValue / totalBudget * 100).toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="expenses">
          <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm mt-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Actual Expenses by Task</h2>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-2">Task Name</th>
                    <th className="px-4 py-2">Budget</th>
                    <th className="px-4 py-2">Personil</th>
                    <th className="px-4 py-2">Equipment</th>
                    <th className="px-4 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {tasksData?.tasks
                    .filter((task: any) => task.resources?.budget)
                    .map((task: any) => (
                      <tr key={task.id} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-2 font-medium text-gray-900 truncate max-w-xs">
                          <span title={task.taskName}>{task.taskName}</span>
                        </td>
                        <td className="px-4 py-2 text-gray-900">Rp {(task.resources?.budget || 0).toLocaleString()}</td>
                        <td className="px-4 py-2 text-gray-600">{task.resources?.personil || 0}</td>
                        <td className="px-4 py-2 text-gray-600">
                          {task.resources?.equipment?.join(', ') || 'None'}
                        </td>
                        <td className="px-4 py-2">
                          <span className={`px-2 py-1 text-xs rounded-full ${task.status === 'completed' ? 'bg-green-100 text-green-800' : task.status === 'in_progress' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                            {task.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="forecast">
          <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm mt-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Cost Forecast</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium text-gray-700 mb-3">Forecast Summary</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                    <span className="text-gray-700">Current Completion</span>
                    <span className="font-medium text-gray-900">{(completionRatio * 100).toFixed(1)}%</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                    <span className="text-gray-700">Forecasted Total Cost</span>
                    <span className="font-medium text-gray-900">Rp {forecastedCost.toLocaleString()}</span>
                  </div>
                  <div className={`flex justify-between items-center p-3 bg-gray-50 rounded ${forecastedCost <= totalBudget ? 'text-green-600' : 'text-red-600'}`}>
                    <span className="font-medium">Forecast Status</span>
                    <span className="font-semibold">{forecastedCost <= totalBudget ? 'On Budget' : 'Over Budget'}</span>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="font-medium text-gray-700 mb-3">Cost Projection</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={forecastData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="actual" stroke="#8884d8" name="Actual Cost" />
                      <Line type="monotone" dataKey="forecast" stroke="#82ca9d" name="Forecast" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
            <div className="mt-6">
              <h3 className="font-medium text-gray-700 mb-3">Cost Trend Analysis</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={costTrendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="planned" fill="#8884d8" name="Planned" />
                    <Bar dataKey="actual" fill="#82ca9d" name="Actual" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}

CostControlPage.displayName = 'CostControlPage'
export default CostControlPage